import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, pairwise, startWith} from 'rxjs/operators';
import {ToolsService} from '../../../../services/tools.service';
import {SearchSuggestItem} from '../../../../types/SearchSuggestItem';
import {SearchSuggestPayload} from '../../../../types/SearchSuggestPayload';
import {SearchWidgetThemeEnum} from '../../enums/search-widget-theme.enum';
import {SearchWidgetManagerService} from '../../../../services/search-widget/search-widget-manager.service';
import {SearchService} from '../../../../services/search/search.service';

@Component({
  selector: 'app-free-search',
  templateUrl: './free-search.component.html',
  styleUrls: ['./free-search.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FreeSearchComponent implements OnInit, OnDestroy {
  types = input<string[]>([]);
  theme = input<SearchWidgetThemeEnum>(SearchWidgetThemeEnum.DEFAULT);
  freeSearchClicked = output<boolean>();
  freeSearchOptionSelected = output<SearchSuggestItem>();
  freeSearchEnterHit = output<string>();
  freeSearchClear = output<boolean>();

  @ViewChild(MatAutocompleteTrigger, {static: false})
  autocomplete: MatAutocompleteTrigger;
  @ViewChild('freeSearchInput') freeSearchInput;
  public freeSearchForm;
  public filteredSuggestItems = signal<SearchSuggestItem[]>(null);
  protected readonly SearchWidgetThemeEnum = SearchWidgetThemeEnum;

  private searchService = inject(SearchService);
  public state = this.searchService.searchWidgetState;
  private searchState = this.searchService.searchState;

  private subscriptions: Subscription[] = [];
  private readonly numberOfSuggestionsToDisplay = 10;
  private isInInitialState = signal<boolean>(true);

  constructor(
    private formBuilder: FormBuilder,
    private tools: ToolsService,
    private searchWidgetManagerService: SearchWidgetManagerService,
  ) {
    this.buildFreeSearchForm();

    effect(
      () => {
        if (this.state()) {
          this.updateFreeSearchFieldValues();
        }
      },
      {allowSignalWrites: true},
    );

    effect(
      () => {
        if (this.state() && this.state().freeSearchFieldFocus === true) {
          this.freeSearchInput.nativeElement.focus();
        }
      },
      {allowSignalWrites: true},
    );
  }

  public ngOnInit(): void {
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public trackByMethod(i: number, el: SearchSuggestItem) {
    return el ? el.suggestValue : null;
  }

  public onEnterHit(): void {
    this.autocomplete.closePanel();
    this.freeSearchEnterHit.emit(this.freeSearchForm.controls['term'].value);
  }

  public onFocus($event: any): void {
    this.isInInitialState.set(false);
    this.freeSearchClicked.emit(true);
    $event.stopPropagation();
  }

  public onClear(): void {
    this.freeSearchForm.reset(
      {
        term: '',
      },
      {emitEvent: false},
    );

    this.freeSearchClear.emit(true);
  }

  public handleOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedId: number = parseInt(event.option.id.split('-')[1], 10);
    const selectedObject: SearchSuggestItem = this.getSuggestItemBySuggestValueOrIndex(event.option.value, selectedId);
    this.freeSearchForm.setValue(
      {
        term: '',
      },
      {emitEvent: false},
    );
    this.freeSearchOptionSelected.emit(selectedObject);
  }

  private updateSuggestions(term: string): void {
    if (term.length > 0 && !this.isInInitialState()) {
      const suggestPayload: SearchSuggestPayload = {
        term: encodeURIComponent(this.tools.clone(term).trim()),
        size: this.numberOfSuggestionsToDisplay,
      };
      if (this.types().length > 0) {
        suggestPayload.types = this.types();
      }
      this.subscriptions.push(
        this.searchService.freeSearchSuggest(suggestPayload).subscribe(
          (result) => this.processSuggestData(result),
          (error) => console.error(error),
        ),
      );
    }
  }

  private processSuggestData(suggestions: SearchSuggestItem[]): void {
    const suggestionsWithLabels: SearchSuggestItem[] = [];
    suggestions.forEach((item) => {
      item.suggestLabel = this.searchWidgetManagerService.getSuggestTypeLabel(item);
      suggestionsWithLabels.push(item);
    });
    this.filteredSuggestItems.set(suggestionsWithLabels);
  }

  private getSuggestItemBySuggestValueOrIndex(suggestValue: string, index?: number): SearchSuggestItem {
    let selectedItem: SearchSuggestItem = null;
    if (!this.tools.isNil(index) && !this.tools.isNil(this.filteredSuggestItems()[index])) {
      selectedItem = this.filteredSuggestItems()[index];
    } else {
      selectedItem = this.filteredSuggestItems().find((item) => item.suggestValue === suggestValue);
    }
    return selectedItem;
  }

  private buildFreeSearchForm(): void {
    this.freeSearchForm = this.formBuilder.group(
      {
        term: [''],
      },
      {emitEvent: false},
    );
  }

  private updateFreeSearchFieldValues(): void {
    if (this.state() && this.freeSearchForm.controls['term'].value !== this.state().autocompleteCurrentValue) {
      if (!this.state().autocompleteValueChanged) {
        this.freeSearchForm.setValue(
          {
            term: this.state().autocompleteCurrentValue || '',
          },
          {emitEvent: false},
        );
      }
      this.updateSuggestions(this.state().autocompleteCurrentValue);
    }
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.freeSearchForm.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged(), startWith(''), pairwise())
        .subscribe(([previous, current]) => {
          const previousTerm = previous.term;
          const currentTerm = current.term;

          if (previousTerm !== currentTerm) {
            this.updateSuggestions(currentTerm);
            this.onValueChanged(currentTerm);
          }
        }),
    );
  }

  private onValueChanged(term: string): void {
    if (this.state().autocompleteCurrentValue !== term) {
      const newState = this.tools.clone(this.state());
      newState.autocompleteValueChanged = true;
      newState.autocompleteCurrentValue = term;
      newState.isLocationSearchVisible = term !== '';

      // const newSearchState = this.tools.clone(this.searchState());
      // newSearchState.holdResultsReload = true;

      this.searchService.updateSearchWidgetState(newState);
      // this.searchService.updateSearchState(newSearchState);
    }
  }
}
